import React, { Suspense, useMemo } from 'react'
import { Card, Tabs, Button, Spinner, Tooltip } from '@design-systems/canvas-ui'
import Icon, { DownloadIcon } from '@design-systems/react-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle } from '@fortawesome/free-solid-svg-icons'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { CustomTooltip, getColor } from 'mvscommoncomponents'
import { getUniqueKeyName } from '../../../../common'
import {
  CODE_RFI_TYPE_QUESTIONNAIRE,
  STATUS_INITIATED,
  STATUS_FASTTRACKED,
} from '../../../../common/commonConstants'
import RFI3PdfGeneratorLazy from '../../../../globalComponents/Rfi3/Rfi3PdfGenerator'

const Rfi3TabsBar = ({
  rfi3Template,
  rfi3Data,
  rfiMasterData,
  statusColorMapper,
  activeTab,
  setActiveTab,
  isTabErrored,
  auditData,
}) => {
  const rfi2Date = useMemo(
    () =>
      rfi3Data?.vetting_status_updated_date
        ? new Date(rfi3Data.vetting_status_updated_date)?.toLocaleDateString()
        : '',
    [rfi3Data?.vetting_status_updated_date],
  )

  const rfi3Date = useMemo(
    () =>
      rfi3Data?.rfi3_vetting_status_updated_date
        ? new Date(
            rfi3Data.rfi3_vetting_status_updated_date,
          )?.toLocaleDateString()
        : '',
    [rfi3Data?.rfi3_vetting_status_updated_date],
  )

  if (!rfi3Template || !rfi3Data) return null

  const handleTabSelect = (selectedTab) => {
    // v8 Tabs passes the selected tab's name (string) or tab object
    const tabName =
      typeof selectedTab === 'string' ? selectedTab : selectedTab?.name

    const tabs = rfi3Template.dynamic_sections?.tabs || []

    const newTabIndex = tabs.findIndex(
      (eachTab) => eachTab.tab_name === tabName,
    )

    if (newTabIndex >= 0) {
      setActiveTab({
        index: newTabIndex,
        uid: tabs[newTabIndex].uid,
        name: tabName,
      })
    }
  }

  const tabs = rfi3Template.dynamic_sections?.tabs || []

  const rfi3AuditTrail = (auditData || []).filter(
    (audit) =>
      audit?.rfi_string?.toLowerCase() ===
      CODE_RFI_TYPE_QUESTIONNAIRE.toLowerCase(),
  )

  const lastInitiatedIndex = rfi3AuditTrail
    .map((audit) => audit?.vetting_status?.toLowerCase())
    .lastIndexOf(STATUS_INITIATED.toLowerCase())

  const currentRfi3Cycle =
    lastInitiatedIndex >= 0 ? rfi3AuditTrail.slice(lastInitiatedIndex) : []

  const isRfi3FastTracked = currentRfi3Cycle.some(
    (audit) =>
      audit?.vetting_status?.toLowerCase() === STATUS_FASTTRACKED.toLowerCase(),
  )

  const isRfi3Completed = [8, 10].includes(
    Number(rfi3Data?.rfi3_vetting_status_code),
  )

  return (
    <Card className="rfi3-tabs-card rfi-tabs-surface">
      <div className="rfi-tabs-row">
        <Tabs
          className="rfi-tabs-control"
          activeTab={activeTab.name}
          disableAdaptive
          onTabSelect={handleTabSelect}
        >
          {tabs.map((eachTab) => (
            <Tabs.Item
              name={eachTab.tab_name}
              key={getUniqueKeyName(eachTab.tab_name, 'tab')}
              data-testid={getUniqueKeyName(eachTab.tab_name, 'tab')}
            >
              <label
                className={'hc-fs-sm font-weight-500'.concat(
                  isTabErrored(eachTab.tab_name) ? ' hc-clr-error' : '',
                )}
              >
                {eachTab.tab_name}
              </label>
            </Tabs.Item>
          ))}
        </Tabs>
        <div className="rfi-tabs-status">
          {/* PDF download for packaging manufacturing type */}
          {rfi3Data?.manufacturing_type_id === 27 && (
            <Suspense
              fallback={
                <Spinner
                  key="spinner-rfi3-loading"
                  data-testid="spinner-rfi3-loading"
                  size="expanded"
                />
              }
            >
              <PDFDownloadLink
                document={
                  <RFI3PdfGeneratorLazy
                    rfi3Data={rfi3Data}
                    rfi3Template={rfi3Template}
                    rfiMasterData={rfiMasterData}
                  />
                }
                fileName="RFI3-Form.pdf"
              >
                {({ loading }) =>
                  loading ? (
                    <Spinner
                      key="spinner-rfi3-loading"
                      data-testid="spinner-rfi3-loading"
                      size="expanded"
                    />
                  ) : (
                    <CustomTooltip
                      title="Download as PDF"
                      placement="top"
                      arrow
                      component={
                        <Button
                          className="button-back hc-ml-min"
                          variant="ghost"
                          size="md"
                          isIconOnly
                          aria-label="Download RFI3 as PDF"
                        >
                          <Icon svg={DownloadIcon} size="inline" aria-hidden />
                        </Button>
                      }
                    />
                  )
                }
              </PDFDownloadLink>
            </Suspense>
          )}

          {rfi3Data?.vetting_status && (
            <p className="hc-pv-normal hc-mb-none">
              {'Status | RFI 2 '}
              <FontAwesomeIcon
                icon={faCircle}
                className="fa-1x hc-mr-dense"
                color={getColor(rfi3Data?.vetting_status, statusColorMapper)}
              />
              <Tooltip content={rfi2Date} location="top">
                {rfi3Data?.vetting_status?.charAt(0)?.toUpperCase() +
                  rfi3Data?.vetting_status?.slice(1)?.toLowerCase()}
              </Tooltip>
            </p>
          )}

          {rfi3Data?.rfi3_vetting_status && (
            <p className="hc-pv-normal hc-mb-none">
              {' | RFI 3 '}
              <FontAwesomeIcon
                icon={faCircle}
                className="fa-1x hc-mr-dense"
                color={getColor(
                  rfi3Data?.rfi3_vetting_status,
                  statusColorMapper,
                )}
              />
              <Tooltip content={rfi3Date} location="top">
                {rfi3Data?.rfi3_vetting_status
                  ?.split(' ')
                  .map(
                    (status) =>
                      status.charAt(0)?.toUpperCase() +
                      status.slice(1)?.toLowerCase() +
                      ' ',
                  )}

                {isRfi3Completed &&
                  (isRfi3FastTracked
                    ? ' (Fast Track / Waived)'
                    : ' (Completed)')}
              </Tooltip>
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}

export default React.memo(Rfi3TabsBar)
